import { create } from 'zustand';
import { conditionalDevtools } from '@/lib/storeMiddleware';

export type OperationState = 'pending' | 'complete' | 'error';

interface OperationStoreState {
  operationStates: Map<string, OperationState>;
  cleanupTimers: Map<string, number>;

  markOperationPending: (emailId: string) => void;
  markOperationComplete: (emailId: string) => void;
  markOperationError: (emailId: string) => void;
  getOperationState: (emailId: string) => OperationState | null;
  clearOperation: (emailId: string) => void;
}

const clearTimer = (timer: number | undefined) => {
  if (timer == null) {
    return;
  }
  if (typeof window !== 'undefined') {
    window.clearTimeout(timer);
  }
};

export const useOperationStore = create<OperationStoreState>()(
  conditionalDevtools(
    (set, get) => ({
      operationStates: new Map(),
      cleanupTimers: new Map(),

      markOperationPending: (emailId) => {
        set((state) => {
          const nextStates = new Map(state.operationStates);
          nextStates.set(emailId, 'pending');
          return { operationStates: nextStates };
        });

        // A hung request must not leave the row's actions disabled forever —
        // clear a stuck 'pending' after a generous timeout (a completion or
        // error mark replaces this timer).
        const existingTimer = get().cleanupTimers.get(emailId);
        clearTimer(existingTimer);
        if (typeof window !== 'undefined') {
          const timer = window.setTimeout(() => {
            if (get().operationStates.get(emailId) === 'pending') {
              get().clearOperation(emailId);
            }
          }, 30000);
          set((state) => {
            const nextTimers = new Map(state.cleanupTimers);
            nextTimers.set(emailId, timer);
            return { cleanupTimers: nextTimers };
          });
        }
      },

      markOperationComplete: (emailId) => {
        set((state) => {
          const nextStates = new Map(state.operationStates);
          nextStates.set(emailId, 'complete');
          return { operationStates: nextStates };
        });

        const existingTimer = get().cleanupTimers.get(emailId);
        clearTimer(existingTimer);

        if (typeof window !== 'undefined') {
          const timer = window.setTimeout(() => {
            get().clearOperation(emailId);
          }, 2000);

          set((state) => {
            const nextTimers = new Map(state.cleanupTimers);
            nextTimers.set(emailId, timer);
            return { cleanupTimers: nextTimers };
          });
        }
      },

      markOperationError: (emailId) => {
        set((state) => {
          const nextStates = new Map(state.operationStates);
          nextStates.set(emailId, 'error');
          return { operationStates: nextStates };
        });

        const existingTimer = get().cleanupTimers.get(emailId);
        clearTimer(existingTimer);

        if (typeof window !== 'undefined') {
          const timer = window.setTimeout(() => {
            get().clearOperation(emailId);
          }, 3000);

          set((state) => {
            const nextTimers = new Map(state.cleanupTimers);
            nextTimers.set(emailId, timer);
            return { cleanupTimers: nextTimers };
          });
        }
      },

      getOperationState: (emailId) => {
        return get().operationStates.get(emailId) ?? null;
      },

      clearOperation: (emailId) => {
        const existingTimer = get().cleanupTimers.get(emailId);
        clearTimer(existingTimer);

        set((state) => {
          const nextStates = new Map(state.operationStates);
          const nextTimers = new Map(state.cleanupTimers);
          nextStates.delete(emailId);
          nextTimers.delete(emailId);
          return { operationStates: nextStates, cleanupTimers: nextTimers };
        });
      },
    }),
    { name: 'OperationStore' }
  )
);
